import { EQUIPMENT_NODE_TYPES, EQUIPMENT_STATUSES, parseEquipmentInput } from '@felix/domain';
import { z } from 'zod';
import type { EquipmentDetail, EquipmentInput, EquipmentNodeType } from './types.js';

/**
 * Add / Edit Equipment çekmecesinin formu (React Hook Form + Zod; CLAUDE.md "Form ve doğrulama").
 * Kurallar domain'in `equipmentInputSchema`'sından gelir (plan Task 1): sunucu aynı şemayla yeniden
 * denetler, burada kullanıcıya erken söylenir. Hata iletisi domain kodudur
 * (`pms.fieldErrors.<KOD>`).
 */

export type NumberField = number | '';

export interface EquipmentFormValues {
  /** `''`: en üst düzey. */
  parentId: string;
  nodeType: EquipmentNodeType;
  code: string;
  name: string;
  /** Equipment Tag No. (ADR 0063 §8). */
  tag: string;
  sfiCode: string;
  maker: string;
  model: string;
  serialNo: string;
  /** `YYYY-MM-DD` ya da `''`. */
  installedOn: string;
  location: string;
  status: (typeof EQUIPMENT_STATUSES)[number];
  critical: boolean;
  criticalReason: string;
  classRelevant: boolean;
  ismRelevant: boolean;
  safetyCritical: boolean;
  outOfPmsManual: boolean;
  outOfPmsReason: string;
  bvItemCode: string;
  surveyPrinciple: string;
  hasCounter: boolean;
  maxDailyHours: NumberField;
  /** Noon Report eşlemesi (K2): yok, ana makine ya da adı verilen yardımcı makine. */
  noonMode: 'none' | 'main_engine' | 'auxiliary';
  noonAuxName: string;
  technical: {
    powerKw: NumberField;
    rpm: NumberField;
    fuelType: string;
    cylinders: NumberField;
    coolingType: string;
    lubricationType: string;
    startingSystem: string;
  };
  notes: string;
}

/** Üst düğüme göre önerilen tür: kökte sistem, sistemin altında ekipman, ondan sonra bileşen. */
function suggestedType(parentType: EquipmentNodeType | null): EquipmentNodeType {
  if (parentType === null) return 'system';
  if (parentType === 'group') return 'system';
  if (parentType === 'system') return 'equipment';
  return 'component';
}

export function emptyEquipmentForm(
  parentId: string | null,
  parentType: EquipmentNodeType | null = null,
): EquipmentFormValues {
  return {
    parentId: parentId ?? '',
    nodeType: suggestedType(parentId === null ? null : parentType),
    code: '',
    name: '',
    tag: '',
    sfiCode: '',
    maker: '',
    model: '',
    serialNo: '',
    installedOn: '',
    location: '',
    status: 'operational',
    critical: false,
    criticalReason: '',
    classRelevant: false,
    ismRelevant: false,
    safetyCritical: false,
    outOfPmsManual: false,
    outOfPmsReason: '',
    bvItemCode: '',
    surveyPrinciple: '',
    hasCounter: false,
    maxDailyHours: 24,
    noonMode: 'none',
    noonAuxName: '',
    technical: {
      powerKw: '',
      rpm: '',
      fuelType: '',
      cylinders: '',
      coolingType: '',
      lubricationType: '',
      startingSystem: '',
    },
    notes: '',
  };
}

const text = (value: string | null | undefined) => value ?? '';
const numberOf = (value: unknown): NumberField => (typeof value === 'number' ? value : '');

export function equipmentFormOf(detail: EquipmentDetail): EquipmentFormValues {
  const noon = detail.noonReportSource;
  const technical = detail.technical ?? {};
  return {
    parentId: detail.parentId ?? '',
    nodeType: detail.nodeType,
    code: detail.code,
    name: detail.name,
    tag: text(detail.tag),
    sfiCode: text(detail.sfiCode),
    maker: text(detail.maker),
    model: text(detail.model),
    serialNo: text(detail.serialNo),
    installedOn: text(detail.installedOn),
    location: text(detail.location),
    status: (EQUIPMENT_STATUSES as readonly string[]).includes(detail.status)
      ? (detail.status as EquipmentFormValues['status'])
      : 'operational',
    critical: detail.critical,
    criticalReason: text(detail.criticalReason),
    classRelevant: detail.classRelevant,
    ismRelevant: detail.ismRelevant,
    safetyCritical: detail.safetyCritical,
    outOfPmsManual: detail.outOfPmsManual,
    outOfPmsReason: text(detail.outOfPmsReason),
    bvItemCode: text(detail.bvItemCode),
    surveyPrinciple: text(detail.surveyPrinciple),
    hasCounter: detail.hasCounterFlag,
    maxDailyHours: detail.maxDailyHours,
    noonMode:
      noon === 'main_engine'
        ? 'main_engine'
        : noon?.startsWith('auxiliary:')
          ? 'auxiliary'
          : 'none',
    noonAuxName: noon?.startsWith('auxiliary:') ? noon.slice('auxiliary:'.length) : '',
    technical: {
      powerKw: numberOf(technical.powerKw),
      rpm: numberOf(technical.rpm),
      fuelType: text(technical.fuelType),
      cylinders: numberOf(technical.cylinders),
      coolingType: text(technical.coolingType),
      lubricationType: text(technical.lubricationType),
      startingSystem: text(technical.startingSystem),
    },
    notes: text(detail.notes),
  };
}

const blank = (value: string) => (value.trim() === '' ? null : value);
const num = (value: NumberField) => (value === '' ? null : value);

function noonSource(values: EquipmentFormValues): string | null {
  if (values.noonMode === 'main_engine') return 'main_engine';
  if (values.noonMode === 'auxiliary') return `auxiliary:${values.noonAuxName.trim()}`;
  return null;
}

/** Formdan sunucunun beklediği girdi (boş metin `null`; sunucu boşlukları yeniden düzenler). */
export function toEquipmentInput(values: EquipmentFormValues): EquipmentInput {
  const t = values.technical;
  return {
    nodeType: values.nodeType,
    code: values.code,
    name: values.name,
    tag: blank(values.tag),
    sfiCode: blank(values.sfiCode),
    maker: blank(values.maker),
    model: blank(values.model),
    serialNo: blank(values.serialNo),
    installedOn: blank(values.installedOn),
    location: blank(values.location),
    status: values.status,
    critical: values.critical,
    criticalReason: values.critical ? blank(values.criticalReason) : null,
    classRelevant: values.classRelevant,
    ismRelevant: values.ismRelevant,
    safetyCritical: values.safetyCritical,
    outOfPmsManual: values.outOfPmsManual,
    outOfPmsReason: values.outOfPmsManual ? blank(values.outOfPmsReason) : null,
    bvItemCode: blank(values.bvItemCode),
    surveyPrinciple: blank(values.surveyPrinciple),
    hasCounter: values.hasCounter,
    maxDailyHours: values.maxDailyHours === '' ? 24 : values.maxDailyHours,
    noonReportSource: noonSource(values),
    technical: {
      powerKw: num(t.powerKw),
      rpm: num(t.rpm),
      fuelType: blank(t.fuelType),
      cylinders: num(t.cylinders),
      coolingType: blank(t.coolingType),
      lubricationType: blank(t.lubricationType),
      startingSystem: blank(t.startingSystem),
    },
    notes: blank(values.notes),
  };
}

/** Domain alan yolu → formun alanı (hata iletisinin çizileceği yer). */
function formPath(path: string, values: EquipmentFormValues): (string | number)[] {
  if (path === 'noonReportSource') {
    return [values.noonMode === 'auxiliary' ? 'noonAuxName' : 'noonMode'];
  }
  return path.split('.');
}

const numberField = z.union([z.number(), z.literal('')]);

/**
 * Formun şeması. `today` geminin yerel günüdür: kurulum tarihi gelecekte olamaz (CLAUDE.md
 * "Zaman"). Kural hatalarını domain'in `parseEquipmentInput`'u verir.
 */
export function equipmentFormSchema(today: string) {
  return z
    .object({
      parentId: z.string(),
      nodeType: z.enum(EQUIPMENT_NODE_TYPES),
      code: z.string(),
      name: z.string(),
      tag: z.string(),
      sfiCode: z.string(),
      maker: z.string(),
      model: z.string(),
      serialNo: z.string(),
      installedOn: z.string(),
      location: z.string(),
      status: z.enum(EQUIPMENT_STATUSES),
      critical: z.boolean(),
      criticalReason: z.string(),
      classRelevant: z.boolean(),
      ismRelevant: z.boolean(),
      safetyCritical: z.boolean(),
      outOfPmsManual: z.boolean(),
      outOfPmsReason: z.string(),
      bvItemCode: z.string(),
      surveyPrinciple: z.string(),
      hasCounter: z.boolean(),
      maxDailyHours: numberField,
      noonMode: z.enum(['none', 'main_engine', 'auxiliary']),
      noonAuxName: z.string(),
      technical: z.object({
        powerKw: numberField,
        rpm: numberField,
        fuelType: z.string(),
        cylinders: numberField,
        coolingType: z.string(),
        lubricationType: z.string(),
        startingSystem: z.string(),
      }),
      notes: z.string(),
    })
    .superRefine((values, ctx) => {
      const parsed = parseEquipmentInput(toEquipmentInput(values), { today });
      if (parsed.ok) return;
      for (const issue of parsed.issues) {
        ctx.addIssue({ code: 'custom', message: issue.code, path: formPath(issue.path, values) });
      }
    });
}
